import React, { useEffect, useRef, useState } from 'react';
import { Search, Users, ToggleLeft, ToggleRight, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient, { authService } from '../../services/authService';

const MIN_SEARCH_LENGTH = 3;

const AdminCustomerSearch = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimer = useRef(null);
  const DEBOUNCE_DELAY = 300;
  const isSearchDisabled = isSearching || searchTerm.trim().length < MIN_SEARCH_LENGTH;

  const clearPendingSearch = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
  };

  const executeSearch = async (term) => {
    const trimmedTerm = term.trim();
    if (trimmedTerm.length < MIN_SEARCH_LENGTH) {
      setCustomers([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await apiClient.post('/admin/customers/search', {
        searchTerm: trimmedTerm
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to search customers');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = () => {
    clearPendingSearch();
    executeSearch(searchTerm);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    const trimmedValue = value.trim();

    if (trimmedValue.length < MIN_SEARCH_LENGTH) {
      clearPendingSearch();
      setCustomers([]);
      return;
    }

    clearPendingSearch();
    debounceTimer.current = setTimeout(() => executeSearch(value), DEBOUNCE_DELAY);
  };

  useEffect(() => () => {
    clearPendingSearch();
  }, []);

  const handleToggleLicense = async (customerId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      const response = await apiClient.put(`/admin/customers/${customerId}/license/toggle`, {
        licenseStatus: newStatus
      });
      
      // Update local state
      setCustomers(prev => prev.map(customer => 
        customer.customerId === customerId 
          ? { ...customer, licenseStatus: newStatus }
          : customer
      ));
      
      toast.success(`License ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      toast.error('Failed to toggle license status');
    }
  };

  const handleResendVerificationEmail = async (customerId) => {
    try {
      await authService.resendVerificationEmail(customerId);
      toast.success('Verification email sent successfully');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to resend verification email';
      toast.error(errorMessage);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'text-green-600 bg-green-100';
      case 'INACTIVE': return 'text-red-600 bg-red-100';
      case 'SUSPENDED': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-8 pt-8 pb-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Customer Search</h1>
          <p className="text-gray-600 mt-1">Search and manage customer licenses</p>
        </div>
        
        <div className="space-y-6">
          {/* Search Section */}
          <div className="flex space-x-4">
            <div className="flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                placeholder="Search by email, WABA phone, or WABA ID..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter at least {MIN_SEARCH_LENGTH} characters to start a search.
              </p>
            </div>
            <button
              onClick={handleSearch}
              disabled={isSearchDisabled}
              className="h-12 w-32 flex items-center justify-center space-x-2 rounded-lg bg-primary-600 px-4 text-white transition-colors hover:bg-primary-700 disabled:bg-gray-400 disabled:text-white focus:outline-none focus-visible:ring-0"
            >
              <Search className="h-4 w-4" />
              <span>{isSearching ? 'Searching...' : 'Search'}</span>
            </button>
          </div>

          {/* Customer List */}
          {customers.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Search Results</h3>
              <div className="space-y-3">
                {customers.map((customer) => (
                  <div key={customer.customerId} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4">
                          <div>
                            <p className="font-medium text-gray-900">{customer.customerEmail}</p>
                            <p className="text-sm text-gray-600">WABA: {customer.wabaPhone}</p>
                            <p className="text-sm text-gray-600">ID: {customer.wabaId}</p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(customer.licenseStatus)}`}>
                            {customer.licenseStatus}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleResendVerificationEmail(customer.customerId)}
                          className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors bg-blue-100 hover:bg-blue-200 text-blue-700"
                          title="Resend verification email"
                        >
                          <Mail className="h-4 w-4" />
                          <span>Resend Email</span>
                        </button>
                        <button
                          onClick={() => handleToggleLicense(customer.customerId, customer.licenseStatus)}
                          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                            customer.licenseStatus === 'ACTIVE'
                              ? 'bg-red-100 hover:bg-red-200 text-red-700'
                              : 'bg-green-100 hover:bg-green-200 text-green-700'
                          }`}
                        >
                          {customer.licenseStatus === 'ACTIVE' ? (
                            <>
                              <ToggleLeft className="h-4 w-4" />
                              <span>Deactivate</span>
                            </>
                          ) : (
                            <>
                              <ToggleRight className="h-4 w-4" />
                              <span>Activate</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {customers.length === 0 && searchTerm && (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No customers found matching your search.</p>
            </div>
          )}

          {!searchTerm && (
            <div className="text-center py-8">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Enter a search term to find customers.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCustomerSearch;





